import { parseBorderValue } from '../parser';
import {
  BORDER_DIRECTIONS,
  BorderSpecificProperty,
  getBorderProperty,
} from '../constants';
import { DecomposeMapEntry } from '../../common/decomposer';

/**
 * Decomposes border-specific properties (borderWidth, borderStyle, borderColor) into
 * their physical atomic properties only.
 *
 * Note: Logical properties (border-inline-start-*, border-inline-end-*) are NOT atomics
 * of these shorthands per CSS spec. They are separate CSS properties.
 */
const decomposeBorderSpecificProperty =
  (property: BorderSpecificProperty) => (value: string) => {
    return Object.fromEntries<string>(
      BORDER_DIRECTIONS.map((direction) => [
        getBorderProperty(direction, property),
        value,
      ]),
    );
  };

const decomposeBorderProperty =
  (
    direction:
      | (typeof BORDER_DIRECTIONS)[number]
      | 'InlineStart'
      | 'InlineEnd'
      | '',
  ) =>
  (value: string): Record<string, string | null> => {
    const border = parseBorderValue(value);

    return {
      [getBorderProperty(direction, 'Width')]: border?.borderWidth ?? null,
      [getBorderProperty(direction, 'Style')]: border?.borderStyle ?? null,
      [getBorderProperty(direction, 'Color')]: border?.borderColor ?? null,
    };
  };

export const borderDecomposeMap: Record<string, DecomposeMapEntry> = {
  border: { fn: decomposeBorderProperty(''), nestingLevel: 0 },
  borderTop: { fn: decomposeBorderProperty('Top'), nestingLevel: 1 },
  borderRight: { fn: decomposeBorderProperty('Right'), nestingLevel: 1 },
  borderBottom: { fn: decomposeBorderProperty('Bottom'), nestingLevel: 1 },
  borderLeft: { fn: decomposeBorderProperty('Left'), nestingLevel: 1 },
  borderInlineStart: {
    fn: decomposeBorderProperty('InlineStart'),
    nestingLevel: 1,
  },
  borderInlineEnd: {
    fn: decomposeBorderProperty('InlineEnd'),
    nestingLevel: 1,
  },
  borderStyle: {
    fn: decomposeBorderSpecificProperty('Style'),
    nestingLevel: 1,
  },
  borderWidth: {
    fn: decomposeBorderSpecificProperty('Width'),
    nestingLevel: 1,
  },
  borderColor: {
    fn: decomposeBorderSpecificProperty('Color'),
    nestingLevel: 1,
  },
};
